"use client";

import { type CSSProperties, useId, useRef } from "react";
import { cn } from "@/lib/utils";

type SurveyScaleSliderProps = {
  label: string;
  sub?: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  lowLabel?: string;
  highLabel?: string;
  compact?: boolean;
};

export default function SurveyScaleSlider({
  label,
  sub,
  value,
  onChange,
  min = 1,
  max = 10,
  step = 1,
  lowLabel,
  highLabel,
  compact = false,
}: SurveyScaleSliderProps) {
  const sliderId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const activePointerIdRef = useRef<number | null>(null);
  const labelId = `${sliderId}-label`;
  const subId = sub ? `${sliderId}-sub` : undefined;
  const range = max - min;
  const stepPrecision = `${step}`.split(".")[1]?.length ?? 0;
  const progressPct =
    range > 0
      ? `${Math.min(100, Math.max(0, ((value - min) / range) * 100))}%`
      : "0%";
  const sliderStyle = {
    ...{
  "--survey-slider-track-h": "0.7rem",
  "--survey-slider-thumb-size": "1.55rem",
  "--survey-slider-fill-start": "color-mix(in oklch, var(--color-primary) 28%, white 72%)",
  "--survey-slider-fill-end": "color-mix(in oklch, var(--color-primary) 44%, white 56%)",
  "--survey-slider-unfilled": "color-mix(in oklch, var(--page-bg-via) 34%, white 66%)",
  "--survey-slider-track-border": "color-mix(in oklch, var(--page-bg-to) 20%, white 80%)",
  "--survey-slider-thumb-border": "color-mix(in oklch, var(--color-primary) 26%, white 74%)",
  "--survey-slider-thumb-shadow": "color-mix(in oklch, var(--color-primary) 14%, transparent)",
  "--survey-slider-thumb-ring": "color-mix(in oklch, var(--color-primary) 18%, transparent)"
},
    "--survey-slider-percent": progressPct,
  } as CSSProperties;

  const getValueFromClientX = (clientX: number) => {
    const input = inputRef.current;
    if (!input || range <= 0) return value;

    const { left, width } = input.getBoundingClientRect();
    if (width <= 0) return value;

    const ratio = Math.min(1, Math.max(0, (clientX - left) / width));
    const rawValue = min + ratio * range;
    const steppedValue = min + Math.round((rawValue - min) / step) * step;
    return Number(
      Math.min(max, Math.max(min, steppedValue)).toFixed(stepPrecision)
    );
  };

  const updateValueFromPointer = (clientX: number) => {
    onChange(getValueFromClientX(clientX));
  };

  const clearActivePointer = () => {
    activePointerIdRef.current = null;
  };

  return (
    <div className={"grid"} data-survey-scale="">
      <label
        className={cn(
          "text-[0.875rem] font-semibold leading-[1.25] text-[color:var(--color-foreground)] 2xl:text-[1rem]",
          compact ? "text-[0.8125rem] 2xl:text-[0.9375rem]" : null
        )}
        id={labelId}
        htmlFor={sliderId}
      >
        {label}
      </label>
      {sub && (
        <p
          className={cn(
            "theme-survey-slider-subtext mt-[0.2rem] text-[0.75rem] leading-[1.45] 2xl:text-[0.875rem]",
            compact ? "text-[0.6875rem] 2xl:text-[0.75rem]" : null
          )}
          id={subId}
        >
          {sub}
        </p>
      )}

      <div
        className={cn(
          "mt-3 flex items-center gap-3 2xl:mt-4 2xl:gap-4",
          compact ? "mt-[0.45rem] gap-[0.625rem] 2xl:mt-[0.6rem] 2xl:gap-3" : null
        )}
      >
        <div className={"relative min-w-0 flex-1"}>
          <input
            ref={inputRef}
            id={sliderId}
            type="range"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(event) => onChange(event.currentTarget.valueAsNumber)}
            onPointerDown={(event) => {
              if (event.button !== 0) return;
              activePointerIdRef.current = event.pointerId;
              event.currentTarget.setPointerCapture(event.pointerId);
              updateValueFromPointer(event.clientX);
              event.preventDefault();
            }}
            onPointerMove={(event) => {
              if (activePointerIdRef.current !== event.pointerId) return;
              updateValueFromPointer(event.clientX);
            }}
            onPointerUp={(event) => {
              if (activePointerIdRef.current !== event.pointerId) return;
              updateValueFromPointer(event.clientX);
              clearActivePointer();
            }}
            onPointerCancel={() => {
              clearActivePointer();
            }}
            aria-labelledby={labelId}
            aria-describedby={subId}
            className={cn("survey-scale-slider", "w-full")}
            style={sliderStyle}
          />
          {(lowLabel || highLabel) && (
            <div
              className={cn(
                "theme-survey-slider-labels mt-[0.35rem] flex justify-between text-[0.6875rem] leading-[1.3] 2xl:text-[0.75rem]",
                compact ? "mt-[0.25rem] text-[0.625rem] 2xl:text-[0.6875rem]" : null
              )}
            >
              <span>{lowLabel}</span>
              <span>{highLabel}</span>
            </div>
          )}
        </div>

        <span
          className={cn(
            "theme-survey-slider-value inline-flex h-7 min-w-10 shrink-0 items-center justify-center rounded-full border text-[0.875rem] font-bold 2xl:h-8 2xl:min-w-[2.8rem] 2xl:text-[1rem]",
            compact ? "h-[1.6rem] min-w-[2.2rem] text-[0.75rem] 2xl:h-7 2xl:min-w-[2.6rem] 2xl:text-[0.875rem]" : null
          )}
        >
          <span className="theme-survey-slider-value-text">{value}</span>
        </span>
      </div>
    </div>
  );
}
